"use client"

import type React from "react"

import { useState } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, ArrowLeft } from "lucide-react"
import { useAuth } from "./auth-context"

type LoginScreenProps = {
  setActiveScreen: (screen: string) => void
  onBackClick?: () => void
}

export function LoginScreen({ setActiveScreen, onBackClick }: LoginScreenProps) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})
  const { login, isLoading } = useAuth()

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {}

    if (!email) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid"
    }

    if (!password) {
      newErrors.password = "Password is required"
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      await login(email, password)
      setActiveScreen("analyze")
    } catch (error) {
      console.error("Login failed:", error)
    }
  }

  return (
    <div className="flex flex-col h-full p-5 pt-8">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <div className="flex items-center mb-4">
          <Button
            variant="ghost"
            size="icon"
            className="mr-2 text-zinc-400 hover:text-white hover:bg-white/10"
            onClick={onBackClick}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-light">
            <span className="gradient-text font-medium">Log In</span>
          </h1>
        </div>
        <p className="text-zinc-400 text-sm">Welcome back! Sign in to your account</p>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-light">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 h-12"
          />
          {errors.email && <p className="text-xs text-rose-400 mt-1">{errors.email}</p>}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="password" className="text-sm font-light">
              Password
            </Label>
            <button
              type="button"
              onClick={() => alert("Password reset functionality would be implemented here")}
              className="text-xs text-rose-400 hover:text-rose-300"
            >
              Forgot password?
            </button>
          </div>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 h-12"
          />
          {errors.password && <p className="text-xs text-rose-400 mt-1">{errors.password}</p>}
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full gradient-bg text-white hover:opacity-90 rounded-xl h-12 font-light"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Signing in...
            </>
          ) : (
            "Sign In"
          )}
        </Button>
      </motion.form>

      <motion.div
        className="mt-8 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <p className="text-zinc-400 text-sm">
          Don't have an account?{" "}
          <button onClick={() => setActiveScreen("signup")} className="text-rose-400 hover:text-rose-300">
            Sign up
          </button>
        </p>
      </motion.div>

      {/* Demo login button for easy testing */}
      <motion.div className="mt-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setEmail("demo@example.com")
            setPassword("password123")
          }}
          className="w-full border-zinc-700 text-zinc-300 rounded-xl hover:bg-white/10 hover:text-white"
        >
          Fill Demo Credentials
        </Button>
      </motion.div>
    </div>
  )
}

